import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiMail, FiLock, FiUserPlus, FiKey, FiAlertCircle } from 'react-icons/fi';
import api from '../api';

const LoginPage = () => {
    const eventTitle = process.env.REACT_APP_EVENT_TITLE || 'EventSawa';
    const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || 'We make events sawa';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || '/dashboard';

    const [loginError, setLoginError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoginError('');
        setIsLoading(true);

        try {
            // console.log('Attempting login with:', { email });
            // Use the login function from AuthContext
            await login(email, password);

            // Redirect to the previous page or dashboard
            // console.log('Login successful, navigating to:', from);
            navigate(from, { replace: true });
        } catch (error) {
            // console.error('Login failed:', error);
            // Prevent the page from refreshing
            e.stopPropagation();
            e.preventDefault();

            // Set a more descriptive error message
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.detail ||
                error.message ||
                'Login failed. Please check your credentials and try again.';

            setLoginError(errorMessage);
            setIsLoading(false);
            return false; // Prevent form submission
        }

        setIsLoading(false);
        return false; // Prevent form submission
    }

    return (
        <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <Helmet>
                <title>{eventTitle}</title> {/* Set page title from environment variable */}
                <meta name="description" content="{eventDescription}" />
            </Helmet>
            <div className="flex flex-col items-center justify-center px-4 py-8 mx-auto md:h-screen lg:py-0 w-full max-w-2xl">
                <div className="w-full bg-white rounded-2xl shadow-xl dark:border md:mt-0 sm:max-w-2xl xl:p-0 dark:bg-gray-800 dark:border-gray-700">
                    <div className="p-8 space-y-6 md:space-y-8 sm:p-8">
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center">
                                <FiUserPlus className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold leading-tight tracking-tight text-gray-900 dark:text-white">
                                Sign in to your account
                            </h1>
                        </div>
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div className="space-y-2">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    <FiMail className="inline-block w-5 h-5 mr-2" />
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className="w-full px-6 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                                    placeholder="name@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    <FiLock className="inline-block w-5 h-5 mr-2" />
                                    Password
                                </label>
                                <input
                                    type="password"
                                    id="password"
                                    className="w-full px-6 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <input
                                            id="remember"
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-indigo-500"
                                        />
                                        <label htmlFor="remember" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                            Remember me
                                        </label>
                                    </div>
                                    <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
                                        <FiKey className="inline-block w-4 h-4 mr-1" />
                                        Forgot password?
                                    </Link>
                                </div>
                            </div>
                            {loginError && (
                                <div className="flex items-center p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800" role="alert">
                                    <FiAlertCircle className="inline mr-2" />
                                    {loginError}
                                </div>
                            )}
                            <div className="space-y-4">
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={`w-full flex items-center justify-center px-10 py-5 rounded-xl text-white font-semibold text-lg transition-all duration-300 ${isLoading
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-lg hover:scale-105'
                                        }`}
                                >
                                    <FiUserPlus className="w-5 h-5 mr-2" />
                                    Sign in
                                </button>
                                <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                                    Don't have an account?{' '}
                                    <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
                                        <FiUserPlus className="inline-block w-4 h-4 mr-1" />
                                        Create account
                                    </Link>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LoginPage;
